/**
 * Button Component
 * Accessible, styled button with variants and loading states
 */

import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from 'react';

// This file uses classic JSX runtime (requires explicit React import)
// This helps Vite React plugin detect JSX transform correctly

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button variant */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';

  /** Button size */
  size?: 'sm' | 'md' | 'lg';

  /** Loading state */
  loading?: boolean;

  /** Full width */
  fullWidth?: boolean;

  /** Icon before text */
  iconBefore?: ReactNode;

  /** Icon after text */
  iconAfter?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      fullWidth = false,
      iconBefore,
      iconAfter,
      children,
      disabled,
      className = '',
      ...props
    },
    ref
  ) => {
    // Base styles
    const baseStyles = 'ww-inline-flex ww-items-center ww-justify-center ww-font-medium ww-rounded-md ww-transition-colors focus:ww-outline-none focus:ww-ring-2 focus:ww-ring-offset-2 disabled:ww-opacity-50 disabled:ww-cursor-not-allowed';

    // Variant styles
    const variantStyles = {
      primary: 'ww-bg-primary ww-text-white hover:ww-bg-primary-hover focus:ww-ring-primary',
      secondary: 'ww-bg-gray-200 ww-text-gray-900 hover:ww-bg-gray-300 focus:ww-ring-gray-500',
      outline: 'ww-border ww-border-gray-300 ww-bg-white ww-text-gray-700 hover:ww-bg-gray-50 focus:ww-ring-primary',
      ghost: 'ww-text-gray-700 hover:ww-bg-gray-100 focus:ww-ring-gray-500',
      danger: 'ww-bg-red-600 ww-text-white hover:ww-bg-red-700 focus:ww-ring-red-500',
    };

    // Size styles
    const sizeStyles = {
      sm: 'ww-px-3 ww-py-1.5 ww-text-sm',
      md: 'ww-px-4 ww-py-2 ww-text-base',
      lg: 'ww-px-6 ww-py-3 ww-text-lg',
    };

    // Width styles
    const widthStyles = fullWidth ? 'ww-w-full' : '';

    const classes = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${widthStyles} ${className}`;

    return (
      <button
        ref={ref}
        className={classes}
        disabled={disabled || loading}
        {...props}
      >
        {/* React import helps Vite plugin detect JSX transform */}
        {loading && (
          <svg
            className="ww-animate-spin ww--ml-1 ww-mr-2 ww-h-4 ww-w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="ww-opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="ww-opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {!loading && iconBefore && (
          <span className="ww-mr-2" aria-hidden="true">
            {iconBefore}
          </span>
        )}
        {children}
        {!loading && iconAfter && (
          <span className="ww-ml-2" aria-hidden="true">
            {iconAfter}
          </span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
